"use client"

import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "~/lib/utils"
import { cva, VariantProps } from "class-variance-authority"
import { Icon } from "./icon"

const tabsListVariants = cva("h-tabs__list", {
  variants: {
    color: {
      lightgrey: "h-tabs--lightgrey",
    },
  },
})
const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Root
    ref={ref}
    className={cn("h-tabs", className)}
    {...props}
  />
))
Tabs.displayName = TabsPrimitive.Root.displayName

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    color?: VariantProps<typeof tabsListVariants>["color"]
  }
>(({ className, color, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(tabsListVariants({ color, className }))}
    {...props}
    asChild={true}
  >
    <ul>{props.children}</ul>
  </TabsPrimitive.List>
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsListItem = React.forwardRef<
  HTMLLIElement,
  React.HTMLAttributes<HTMLLIElement>
>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn("h-tabs__item", className)} {...props} />
))
TabsListItem.displayName = "TabsListItem"

const tabsTriggerVariants = cva("h-tab", {
  variants: {
    active: {
      true: "h-tab--active",
    },
  },
})
const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
    active?: boolean
    stepNumber?: number
    completed?: boolean
  }
>(({ className, active, stepNumber, completed, children, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(tabsTriggerVariants({ active, className }))}
    {...props}
  >
    {completed ? (
      <Icon icon="circle-check" className="blue" size="sm" aria-label={i18n.t("js.email_accounts.provisioning.step_completed")} />
    ) : stepNumber !== undefined ? (
      <div className="h-tab__count">{stepNumber}</div>
    ) : null}
    {children}
  </TabsPrimitive.Trigger>
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsTriggerCount = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }) => (
  <span className={cn("h-tab__count", className)} {...props} />
))
TabsTriggerCount.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className,
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export {
  Tabs,
  TabsList,
  TabsListItem,
  TabsTrigger,
  TabsTriggerCount,
  TabsContent,
}
